import { useState } from 'react';
import { useTranslation } from "react-i18next";
import { useFeatureGate } from '../../hooks/useFeatureGate';
import { usePlanLimits } from '../../hooks/usePlanLimits';

interface ContextualFABProps {
  className?: string;
  currentPath: string;
  searchParams: URLSearchParams;
  navigate: (path: string, options?: any) => void;
}

interface ActionItem {
  label: string;
  icon: string;
  action: () => void;
  blocked?: boolean;
  badge?: string;
}

export default function ContextualFAB({
 className = '', currentPath, searchParams, navigate }: ContextualFABProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const isViewMode = searchParams.get('view') === '1';
  const { promptUpgrade } = useFeatureGate();
  const { planInfo } = usePlanLimits();
  
  // Verificar límites de IA
  const checkAILimit = (featureName: string, description: string, callback: () => void) => {
    const aiLimit = (planInfo?.limits as any)?.aiGenerationsPerMonth || 0;
    if (aiLimit === 0 || aiLimit === -1) {
      promptUpgrade(featureName, description);
      return;
    }
    callback();
  };
  
  // Helper para verificar si IA está bloqueada
  const isAIBlocked = () => {
    const aiLimit = (planInfo?.limits as any)?.aiGenerationsPerMonth || 0;
    return aiLimit === 0 || aiLimit === -1;
  };

  // Helper para crear evento
  const toCalendarCreate = () => {
    if (currentPath === '/calendar' || currentPath === '/groups/calendar') {
      try {
        const btn = document.querySelector('[data-calendar-create]') as HTMLButtonElement | null;
        if (btn) {
          btn.click();
          return;
        }
      } catch {
      }
    }
    try {
      const mid = typeof window !== 'undefined' ? localStorage.getItem('active_member_id') : null;
      const qs = new URLSearchParams({ new: '1' });
      if (mid) qs.set('memberId', mid);
      navigate(`/groups/calendar?${qs.toString()}`);
    } catch {
      navigate('/groups/calendar?new=1');
    }
  };

  // Helper para abrir modal de importación con IA (foto) en calendario
  const toCalendarWithAI = () => {
    checkAILimit(
      t('calendar.aiGeneratorName', { defaultValue: 'AI Event Generator' }),
      t('calendar.aiGeneratorDescription', { defaultValue: 'Automatically generate events with AI' }),
      () => {
        if (currentPath === '/calendar' || currentPath === '/groups/calendar') {
          try {
            const btn = document.querySelector('[data-calendar-import-ai]') as HTMLButtonElement | null;
            if (btn) {
              btn.click();
              return;
            }
          } catch {
          }
        }
        const nonce = Date.now();
        navigate(`/groups/calendar?openAI=1&_=${nonce}`);
      }
    );
  };

  // Helper para abrir modal de importación con IA (foto) en recetas
  const toRecipesWithAI = () => {
    checkAILimit(
      t('recipes.aiGeneratorTitle', { defaultValue: 'AI Recipe Generator' }),
      t('recipes.aiGeneratorDesc', { defaultValue: 'Automatically generate recipes with AI' }),
      () => navigate('/family/recipes?openAI=1')
    );
  };

  // Función para obtener las acciones según la ruta actual
  const getContextualActions = (): ActionItem[] | null => {
    const path = currentPath;

    // Tablero Grupal - acciones completas con IA
    if (path === '/groups/home-groups' || path === '/dashboard') {
      const aiBlocked = isAIBlocked();
      return [
        { label: t('fab.createMember', { defaultValue: 'Create member' }) as string, icon: '👤', action: () => navigate('/groups/members/new') },
        { label: t('fab.createActivity', { defaultValue: 'Create activity' }) as string, icon: '⚡', action: () => navigate('/groups/activities/new') },
        { label: t('fab.createTask', { defaultValue: 'Create task' }) as string, icon: '✅', action: () => navigate('/groups/checklist/new') },
        { label: t('calendar.crearEvento', { defaultValue: 'Create event' }) as string, icon: '📅', action: toCalendarCreate },
        { label: t('calendar.importWithAI', { defaultValue: 'Import with AI' }) as string, icon: '📸', action: toCalendarWithAI, blocked: aiBlocked, badge: aiBlocked ? '🔒' : undefined },
        { label: t('fab.createRecipe', { defaultValue: 'Create recipe' }) as string, icon: '🍳', action: () => navigate('/family/recipes/create') },
        { label: t('fab.recipeWithAI', { defaultValue: 'Recipe with AI' }) as string, icon: '📷', action: toRecipesWithAI, blocked: aiBlocked, badge: aiBlocked ? '🔒' : undefined },
        { label: t('fab.createReward', { defaultValue: 'Create reward' }) as string, icon: '🎁', action: () => navigate('/rewards/manage/new') },
      ];
    }

    // Tablero Personal, Planificador, Lista de Compras - acciones rápidas
    if (path === '/groups/home-personal' || path === '/groups/dashboard' || path === '/family/meal-planner' || path === '/family/shopping-list') {
      const aiBlocked = isAIBlocked();
      return [
        { label: t('fab.createActivity', { defaultValue: 'Create activity' }) as string, icon: '⚡', action: () => navigate('/groups/activities/new') },
        { label: t('fab.createTask', { defaultValue: 'Create task' }) as string, icon: '✅', action: () => navigate('/groups/checklist/new') },
        { label: t('calendar.crearEvento', { defaultValue: 'Create event' }) as string, icon: '📅', action: toCalendarCreate },
        { label: t('calendar.importWithAI', { defaultValue: 'Import with AI' }) as string, icon: '📸', action: toCalendarWithAI, blocked: aiBlocked, badge: aiBlocked ? '🔒' : undefined },
        { label: t('fab.createRecipe', { defaultValue: 'Create recipe' }) as string, icon: '🍳', action: () => navigate('/family/recipes/create') },
        { label: t('fab.recipeWithAI', { defaultValue: 'Recipe with AI' }) as string, icon: '📷', action: toRecipesWithAI, blocked: aiBlocked, badge: aiBlocked ? '🔒' : undefined },
      ];
    }

    // Todas las rutas de Actividades (lista, new, edit, edit?view=1) → CREAR
    if (path.startsWith('/groups/activities') || path.startsWith('/groups/actividades')) {
      return [
        { label: t('fab.createActivity', { defaultValue: 'Create activity' }) as string, icon: '⚡', action: () => navigate('/groups/activities/new') }
      ];
    }

    // Todas las rutas de Checklist (lista, new, edit, edit?view=1) → CREAR
    if (path.startsWith('/groups/checklist')) {
      return [
        { label: t('fab.createTask', { defaultValue: 'Create task' }) as string, icon: '✅', action: () => navigate('/groups/checklist/new') }
      ];
    }

    // Rutinas (lista, new, edit) → CREAR
    if (path.startsWith('/groups/routines')) {
      return [
        { label: t('routines.form.createRoutine', { defaultValue: 'Create routine' }) as string, icon: '🗓️', action: () => navigate('/groups/routines/new') }
      ];
    }

    // Calendar
    if (path === '/calendar' || path === '/groups/calendar') {
      const aiBlocked = isAIBlocked();
      return [
        { label: t('calendar.crearEvento', { defaultValue: 'Create event' }) as string, icon: '📅', action: () => {
          try {
            const btn = document.querySelector('[data-calendar-create]') as HTMLButtonElement | null;
            if (btn) { btn.click(); return; }
          } catch {}
          navigate('/groups/calendar?new=1');
        }},
        { label: t('calendar.importWithAI', { defaultValue: 'Import with AI' }) as string, icon: '📸', action: toCalendarWithAI, blocked: aiBlocked, badge: aiBlocked ? '🔒' : undefined },
      ];
    }

    // Board
    if (path === '/groups/board') {
      const aiBlocked = isAIBlocked();
      return [
        { label: t('fab.createTask', { defaultValue: 'Create task' }) as string, icon: '✅', action: () => navigate('/groups/checklist/new') },
        { label: t('fab.createActivity', { defaultValue: 'Create activity' }) as string, icon: '⚡', action: () => navigate('/groups/activities/new') },
        { label: t('calendar.crearEvento', { defaultValue: 'Create event' }) as string, icon: '📅', action: toCalendarCreate },
        { label: t('calendar.importWithAI', { defaultValue: 'Import with AI' }) as string, icon: '📸', action: toCalendarWithAI, blocked: aiBlocked, badge: aiBlocked ? '🔒' : undefined },
      ];
    }

    // Templates / Rutinas - ir a biblioteca
    if (path === '/templates' || path.startsWith('/templates')) {
      return [
        { label: t('fab.viewLibrary', { defaultValue: 'View library' }) as string, icon: '📚', action: () => navigate('/templates') }
      ];
    }

    // Recetas - crear o importar con IA
    if (path === '/family/recipes' || path.startsWith('/family/recipes')) {
      const aiBlocked = isAIBlocked();
      return [
        { label: t('fab.createRecipe', { defaultValue: 'Create recipe' }) as string, icon: '🍳', action: () => navigate('/family/recipes/create') },
        { label: t('fab.recipeWithAI', { defaultValue: 'Recipe with AI' }) as string, icon: '📷', action: toRecipesWithAI, blocked: aiBlocked, badge: aiBlocked ? '🔒' : undefined },
      ];
    }

    // RewardManager
    if (path === '/rewards/manage' || path.startsWith('/rewards')) {
      return [
        { label: t('fab.createReward', { defaultValue: 'Create reward' }) as string, icon: '🎁', action: () => navigate('/rewards/manage/new') }
      ];
    }

    // Todas las rutas de Grupos (lista, new, edit, edit?view=1) → CREAR
    if (path.startsWith('/groups/groups')) {
      return [
        { label: t('fab.createGroup', { defaultValue: 'Create group' }) as string, icon: '👨‍👩‍👧‍👦', action: () => navigate('/groups/groups/new') }
      ];
    }

    // Todas las rutas de Integrantes (lista, new, edit, edit?view=1) → CREAR
    if (path.startsWith('/groups/members')) {
      return [
        { label: t('fab.createMember', { defaultValue: 'Create member' }) as string, icon: '👤', action: () => navigate('/groups/members/new') }
      ];
    }

    // Todas las rutas de Temporadas (lista, new, edit) → CREAR
    if (path.startsWith('/groups/seasons')) {
      return [
        { label: t('fab.createSeason', { defaultValue: 'Create season' }) as string, icon: '🏫', action: () => navigate('/groups/seasons/new') }
      ];
    }

    return null;
  };

  const actions = getContextualActions();

  // Si no hay acciones para esta ruta, no mostrar nada
  if (!actions || actions.length === 0) return null;

  // Si solo hay una acción, ejecutarla directamente
  const handleClick = () => {
    if (actions.length === 1) {
      actions[0].action();
    } else {
      setIsOpen(true);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleActionClick = (action: ActionItem) => {
    action.action();
    handleClose();
  };

  return (
    <>
      {/* Botón Central - SIEMPRE CREAR */}
      <button
        onClick={handleClick}
        className={`flex flex-col items-center justify-center transition-colors ${className}`}
      >
        <div className="flex items-center justify-center w-18 h-18 sm:w-20 sm:h-20 bg-[#AAD08C] rounded-full shadow-[0_8px_16px_rgba(170,208,140,0.4)] hover:shadow-[0_12px_24px_rgba(170,208,140,0.5)] active:scale-95 transition-all hover:scale-105">
          {/* Siempre ícono de + para crear */}
          <svg className="w-9 h-9 sm:w-10 sm:h-10 text-white dark:text-gray-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </div>
        {/* Label visible como los otros */}
        <span className="text-[10px] sm:text-xs font-black uppercase tracking-wider text-gray-400 dark:text-gray-500">{t('common.create', { defaultValue: 'Create' })}</span>
      </button>

      {/* Modal de Opciones Múltiples */}
      {isOpen && actions.length > 1 && (
        <>
          {/* Overlay */}
          <div
            className="fixed inset-0 bg-black/50 z-50 lg:hidden backdrop-blur-sm animate-in fade-in duration-200"
            onClick={handleClose}
          />

          {/* Menu Modal */}
          <div
            className="fixed inset-x-0 bottom-0 bg-white dark:bg-gray-900 rounded-t-3xl shadow-2xl z-50 lg:hidden animate-in slide-in-from-bottom duration-300"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 64px)', maxHeight: '75vh' }}
          >
            {/* Handle bar */}
            <div className="flex justify-center pt-4 pb-2">
              <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-700 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {t('common.create', { defaultValue: 'Create' })}
                </h2>
                <button
                  onClick={handleClose}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Action Items */}
            <div className="overflow-y-auto px-4 py-2" style={{ maxHeight: 'calc(70vh - 120px)' }}>
              <div className="space-y-1">
                {actions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => handleActionClick(action)}
                    className="w-full flex items-center gap-4 px-4 py-3 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors text-left relative"
                  >
                    <span className="text-2xl">{action.icon}</span>
                    <span className="flex-1 text-base font-medium">{action.label}</span>
                    {action.badge && (
                      <span className="text-xl">{action.badge}</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
